export const CUSTOMER_OVERLAP_COMPONENT_KINDS = [
  'cross_sell',
  'pricing',
  'coverage_dedup',
  'retention_risk',
] as const;

export type CustomerOverlapComponentKind =
  (typeof CUSTOMER_OVERLAP_COMPONENT_KINDS)[number];

export type CustomerOverlapBands = Readonly<{
  conservativeRunRateUsd: number;
  baseRunRateUsd: number;
  stretchRunRateUsd: number;
}>;

export type CustomerOverlapOwnerSeat = Readonly<{
  seatId: string;
  combinedSeatId: string;
  orgStateId: string;
  title: string;
  fullyLoadedAnnualCostUsd: number;
}>;

export type CustomerOverlapAccount = Readonly<{
  accountId: string;
  accountName: string;
  certaintyClass: 'c2_modelled_upside';
  chrwRevenueByServiceLine: Readonly<{
    surfaceTransportationUsd: number;
    managedServicesUsd: number;
  }>;
  phoenixRevenueByServiceLine: Readonly<{
    oceanForwardingUsd: number;
    airForwardingUsd: number;
    customsBrokerageUsd: number;
  }>;
  grossMarginPct: Readonly<{ chrw: number; phoenix: number }>;
  contractExpiry: string;
  ownerSeats: Readonly<{
    chrw: CustomerOverlapOwnerSeat;
    phoenix: CustomerOverlapOwnerSeat;
  }>;
  coverageDedupSeatSide: 'chrw' | 'phoenix';
  bands: Readonly<{
    crossSell: CustomerOverlapBands;
    pricing: CustomerOverlapBands;
    coverageDedup: CustomerOverlapBands;
    retentionRisk: CustomerOverlapBands;
  }>;
  bandBasis: string;
}>;

export type CustomerOverlapAccountRegister = Readonly<{
  familyId: string;
  accounts: readonly CustomerOverlapAccount[];
}>;

export type CustomerOverlapLeverExtension = Readonly<{
  familyId: string;
  component: CustomerOverlapComponentKind;
  accountRegister?: CustomerOverlapAccountRegister;
}>;

export function validateCustomerOverlapLeverExtension(
  value: unknown,
  schemaIsV2: boolean,
  errors: string[],
): void {
  if (value === undefined) return;
  if (!schemaIsV2) {
    errors.push('customerOverlap requires ma_synergy_commitment_v2');
    return;
  }
  if (!isRecord(value)) {
    errors.push('customerOverlap must be an object');
    return;
  }
  validateText(value.familyId, 'customerOverlap.familyId', errors);
  validateComponent(value.component, errors);
  validateAccountRegister(value, errors);
}

function validateComponent(value: unknown, errors: string[]): void {
  if (
    typeof value !== 'string' ||
    !CUSTOMER_OVERLAP_COMPONENT_KINDS.includes(
      value as CustomerOverlapComponentKind,
    )
  ) {
    errors.push('customerOverlap.component is invalid');
  }
}

function validateAccountRegister(
  value: Record<string, unknown>,
  errors: string[],
): void {
  const component = value.component;
  const register = value.accountRegister;
  if (component === 'coverage_dedup' && register === undefined) {
    errors.push(
      'customerOverlap.accountRegister is required for coverage_dedup',
    );
    return;
  }
  if (component !== 'coverage_dedup' && register !== undefined) {
    errors.push(
      'customerOverlap.accountRegister is only allowed for coverage_dedup',
    );
    return;
  }
  if (register === undefined) return;
  if (!isRecord(register)) {
    errors.push('customerOverlap.accountRegister must be an object');
    return;
  }
  validateText(
    register.familyId,
    'customerOverlap.accountRegister.familyId',
    errors,
  );
  if (register.familyId !== value.familyId) {
    errors.push(
      'customerOverlap.accountRegister.familyId must match customerOverlap.familyId',
    );
  }
  if (!Array.isArray(register.accounts) || register.accounts.length === 0) {
    errors.push(
      'customerOverlap.accountRegister.accounts must include at least one account',
    );
    return;
  }
  register.accounts.forEach((account, index) =>
    validateAccount(account, index, errors),
  );
}

function validateAccount(
  value: unknown,
  index: number,
  errors: string[],
): void {
  const path = `customerOverlap.accountRegister.accounts[${index}]`;
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  validateText(value.accountId, `${path}.accountId`, errors);
  validateText(value.accountName, `${path}.accountName`, errors);
  if (value.certaintyClass !== 'c2_modelled_upside') {
    errors.push(`${path}.certaintyClass must be c2_modelled_upside`);
  }
  validateRevenue(
    value.chrwRevenueByServiceLine,
    `${path}.chrwRevenueByServiceLine`,
    ['surfaceTransportationUsd', 'managedServicesUsd'],
    errors,
  );
  validateRevenue(
    value.phoenixRevenueByServiceLine,
    `${path}.phoenixRevenueByServiceLine`,
    ['oceanForwardingUsd', 'airForwardingUsd', 'customsBrokerageUsd'],
    errors,
  );
  validateMargin(value.grossMarginPct, `${path}.grossMarginPct`, errors);
  validateText(value.contractExpiry, `${path}.contractExpiry`, errors);
  validateSeats(value.ownerSeats, `${path}.ownerSeats`, errors);
  if (
    value.coverageDedupSeatSide !== 'chrw' &&
    value.coverageDedupSeatSide !== 'phoenix'
  ) {
    errors.push(`${path}.coverageDedupSeatSide is invalid`);
  }
  validateAccountBands(value.bands, `${path}.bands`, errors);
  validateText(value.bandBasis, `${path}.bandBasis`, errors);
}

function validateRevenue(
  value: unknown,
  path: string,
  fields: readonly string[],
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  fields.forEach((field) =>
    validateNonNegative(value[field], `${path}.${field}`, errors),
  );
}

function validateMargin(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  ['chrw', 'phoenix'].forEach((field) => {
    const number = validateNonNegative(value[field], `${path}.${field}`, errors);
    if (number !== null && number > 100) {
      errors.push(`${path}.${field} must be at most 100`);
    }
  });
}

function validateSeats(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  ['chrw', 'phoenix'].forEach((side) =>
    validateSeat(value[side], `${path}.${side}`, errors),
  );
}

function validateSeat(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  ['seatId', 'combinedSeatId', 'orgStateId', 'title'].forEach((field) =>
    validateText(value[field], `${path}.${field}`, errors),
  );
  validateNonNegative(
    value.fullyLoadedAnnualCostUsd,
    `${path}.fullyLoadedAnnualCostUsd`,
    errors,
  );
}

function validateAccountBands(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  ['crossSell', 'pricing', 'coverageDedup', 'retentionRisk'].forEach((field) =>
    validateSignedBands(value[field], `${path}.${field}`, errors),
  );
}

function validateSignedBands(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${path} must be an object`);
    return;
  }
  const conservative = validateFinite(
    value.conservativeRunRateUsd,
    `${path}.conservativeRunRateUsd`,
    errors,
  );
  const base = validateFinite(
    value.baseRunRateUsd,
    `${path}.baseRunRateUsd`,
    errors,
  );
  const stretch = validateFinite(
    value.stretchRunRateUsd,
    `${path}.stretchRunRateUsd`,
    errors,
  );
  if (
    conservative !== null &&
    base !== null &&
    stretch !== null &&
    !(conservative <= base && base <= stretch)
  ) {
    errors.push(`${path} must satisfy conservative <= base <= stretch`);
  }
}

function validateText(
  value: unknown,
  path: string,
  errors: string[],
): void {
  if (typeof value !== 'string' || value.trim().length === 0) {
    errors.push(`${path} is required`);
  }
}

function validateNonNegative(
  value: unknown,
  path: string,
  errors: string[],
): number | null {
  const number = validateFinite(value, path, errors);
  if (number !== null && number < 0) {
    errors.push(`${path} must be non-negative`);
  }
  return number;
}

function validateFinite(
  value: unknown,
  path: string,
  errors: string[],
): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    errors.push(`${path} must be a finite number`);
    return null;
  }
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
